/**
 * AuditLogScreen — paginated browse of the audit_log table.
 *
 * Backed by `GET /system/audit-log`. When the endpoint returns no data we
 * gracefully surface an EmptyState.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ScrollText } from 'lucide-react';

import { apiGet, ApiError } from '../../lib/api';
import { formatDateTime } from '../../lib/locale';
import { Card, CardHeader } from '../../components/Card';
import { EmptyState } from '../../components/EmptyState';
import { Spinner } from '../../components/Spinner';

// ---------------------------------------------------------------------------
// Types — loose mirror of (future) backend schema
// ---------------------------------------------------------------------------

interface AuditEntry {
  id: string;
  occurred_at: string;
  actor_email: string | null;
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  actor_ip: string | null;
  tenant_slug: string | null;
}

interface AuditPage {
  data: AuditEntry[];
  meta: { page: number; per_page: number; total: number; pages: number };
}

interface Filters {
  action: string;
  actor: string;
  fromDate: string;
  toDate: string;
}

const PER_PAGE = 50;

// ---------------------------------------------------------------------------

export function AuditLogScreen(): JSX.Element {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Filters>({
    action: '',
    actor: '',
    fromDate: '',
    toDate: '',
  });

  const queryString = useMemo(() => {
    const params = new URLSearchParams({
      page: String(page),
      per_page: String(PER_PAGE),
    });
    if (filters.action.trim()) params.set('action', filters.action.trim());
    if (filters.actor.trim()) params.set('actor', filters.actor.trim());
    if (filters.fromDate) params.set('from', filters.fromDate);
    if (filters.toDate) params.set('to', filters.toDate);
    return params.toString();
  }, [page, filters]);

  const query = useQuery({
    queryKey: ['admin', 'audit-log', queryString],
    queryFn: () => apiGet<AuditPage>(`/system/audit-log?${queryString}`),
    retry: false,
  });

  const is404 = query.error instanceof ApiError && query.error.status === 404;

  return (
    <div className="space-y-4 px-6 py-5">
      <header>
        <h1 className="text-lg font-semibold text-slate-900">Audit-log</h1>
        <p className="mt-0.5 text-[12px] text-slate-500">
          Trail van admin-acties, login-pogingen en cross-tenant queries.
        </p>
      </header>

      <Card>
        <CardHeader title="Filters" />
        <div className="grid grid-cols-1 gap-3 px-4 py-3 sm:grid-cols-2 lg:grid-cols-4">
          <FilterField label="Actie">
            <input
              type="text"
              value={filters.action}
              placeholder="bv. user.create"
              onChange={(e) => {
                setPage(1);
                setFilters({ ...filters, action: e.target.value });
              }}
              className={inputCls}
            />
          </FilterField>
          <FilterField label="Actor (e-mail)">
            <input
              type="text"
              value={filters.actor}
              placeholder="bv. pieter@serso.be"
              onChange={(e) => {
                setPage(1);
                setFilters({ ...filters, actor: e.target.value });
              }}
              className={inputCls}
            />
          </FilterField>
          <FilterField label="Vanaf">
            <input
              type="date"
              value={filters.fromDate}
              onChange={(e) => {
                setPage(1);
                setFilters({ ...filters, fromDate: e.target.value });
              }}
              className={inputCls}
            />
          </FilterField>
          <FilterField label="Tot">
            <input
              type="date"
              value={filters.toDate}
              onChange={(e) => {
                setPage(1);
                setFilters({ ...filters, toDate: e.target.value });
              }}
              className={inputCls}
            />
          </FilterField>
        </div>
      </Card>

      <Card>
        <CardHeader
          title="Gebeurtenissen"
          subtitle={query.data ? `${query.data.meta.total} totaal` : undefined}
        />
        {query.isLoading ? (
          <div className="flex justify-center px-6 py-12">
            <Spinner />
          </div>
        ) : is404 ? (
          <EmptyState
            icon={<ScrollText size={24} aria-hidden />}
            title="Geen audit-events"
            description="Er zijn nog geen audit-events voor de huidige filters."
          />
        ) : query.isError ? (
          <EmptyState
            title="Kon audit-log niet ophalen"
            description={query.error instanceof Error ? query.error.message : 'Onbekende fout.'}
          />
        ) : !query.data || query.data.data.length === 0 ? (
          <EmptyState
            title="Geen gebeurtenissen"
            description="Er zijn geen audit-events die aan deze filters voldoen."
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                    <th className="px-3 py-2">Tijdstip</th>
                    <th className="px-3 py-2">Actor</th>
                    <th className="px-3 py-2">Actie</th>
                    <th className="px-3 py-2">Resource</th>
                    <th className="px-3 py-2">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {query.data.data.map((entry) => (
                    <tr key={entry.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-2 font-mono tabular-nums text-slate-700">
                        {formatDateTime(entry.occurred_at)}
                      </td>
                      <td className="px-3 py-2 font-mono text-slate-900">
                        {entry.actor_email ?? '—'}
                      </td>
                      <td className="px-3 py-2 text-slate-700">{entry.action}</td>
                      <td className="px-3 py-2 font-mono text-slate-600">
                        {entry.resource_type
                          ? `${entry.resource_type}/${entry.resource_id ?? '?'}`
                          : '—'}
                      </td>
                      <td className="px-3 py-2 font-mono text-slate-600">
                        {entry.actor_ip ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination page={page} pages={query.data.meta.pages} onChange={setPage} />
          </>
        )}
      </Card>
    </div>
  );
}

function FilterField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}

function Pagination({
  page,
  pages,
  onChange,
}: {
  page: number;
  pages: number;
  onChange: (p: number) => void;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 text-[11px] text-slate-600">
      <span>
        Pagina {page} van {pages || 1}
      </span>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
          className="rounded px-2 py-1 hover:bg-slate-100 disabled:opacity-40"
        >
          Vorige
        </button>
        <button
          type="button"
          disabled={page >= pages}
          onClick={() => onChange(page + 1)}
          className="rounded px-2 py-1 hover:bg-slate-100 disabled:opacity-40"
        >
          Volgende
        </button>
      </div>
    </div>
  );
}

const inputCls =
  'w-full px-2.5 py-1.5 text-[12px] bg-white border border-slate-300 rounded focus:border-brand-600 focus:outline-none focus:ring-1 focus:ring-brand-600';
